import React, { useContext, useEffect, useState } from "react";
import { Pagination } from "antd";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import useGet from "../customHooks/useGet";
import usePost from "../customHooks/usePost";
import usePost2 from "../customHooks/usePost2";
import { MainLanguageContext } from "../context/MainLanguageContext";
import { FaCar, FaGem, FaLeaf, FaShieldAlt, FaTruck } from "react-icons/fa";

const vehicleTypeConfig = {
  economy: { label: "Economy", icon: FaLeaf },
  midsize: { label: "Midsize", icon: FaCar },
  crossovers: { label: "Crossovers", icon: FaCar },
  suv: { label: "SUV", icon: FaShieldAlt },
  luxury: { label: "Luxury", icon: FaGem },
  commercial: { label: "Commercial", icon: FaTruck },
};

const normalizeVehicleType = (value = "") =>
  String(value).toLowerCase().replace(/[^a-z0-9]/g, "");

const formatVehicleType = (value = "") =>
  String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const BulkUpdatePrices = () => {
  const { mainLanguage } = useContext(MainLanguageContext);
  const [products, setProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0 });
  const [searchValue, setSearchValue] = useState("");
  const [selectedVehicleType, setSelectedVehicleType] = useState("all");
  const [prices, setPrices] = useState({
    daily_price: "",
    weekly_price: "",
    monthly_price: "",
  });
  const [resget, apiMethodGet] = useGet();
  const [searchRes, apiMethodSearch] = usePost();
  const [updateRes, updatePrices] = usePost2();

  const loadProducts = (page = 1, search = "") => {
    const value = search.trim();
    setCurrentPage(page);

    if (value) {
      const formData = new FormData();
      formData.append("search_query", value);
      apiMethodSearch(`products/search_products_list/${mainLanguage}/1000?page=${page}`, formData);
    } else {
      apiMethodGet(`products/list/${mainLanguage}/1000?page=${page}`);
    }
  };

  useEffect(() => {
    if (mainLanguage) {
      setSearchValue("");
      setSelectedIds([]);
      loadProducts(1, "");
    }
  }, [mainLanguage]);

  useEffect(() => {
    if (resget.data) {
      setProducts(Array.isArray(resget.data.data) ? resget.data.data : []);
      setPagination(resget.data.pagination || { total: 0 });
    }
  }, [resget.data]);

  useEffect(() => {
    if (searchRes.data) {
      setProducts(Array.isArray(searchRes.data.data) ? searchRes.data.data : []);
      setPagination(searchRes.data.pagination || { total: 0 });
    }
  }, [searchRes.data]);

  useEffect(() => {
    if (updateRes.data) {
      toast.success(updateRes.data.message || "Product prices updated successfully.");
      setSelectedIds([]);
    }
    if (updateRes.error) {
      toast.error(updateRes.error?.response?.data?.message || "Unable to update product prices.");
    }
  }, [updateRes.data, updateRes.error]);

  const toggleProduct = (id) => {
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    );
  };

  const toggleVisibleProducts = () => {
    const visibleIds = products.map((product) => product.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

    setSelectedIds((current) =>
      allVisibleSelected
        ? current.filter((id) => !visibleIds.includes(id))
        : [...new Set([...current, ...visibleIds])]
    );
  };

  const handlePriceChange = (event) => {
    const { name, value } = event.target;
    setPrices((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const enteredPrices = Object.entries(prices).filter(([, value]) => value !== "");

    if (!selectedIds.length) {
      toast.warning("Please select at least one product or car.");
      return;
    }
    if (!enteredPrices.length) {
      toast.warning("Please enter at least one price.");
      return;
    }
    if (enteredPrices.some(([, value]) => Number(value) < 0 || Number.isNaN(Number(value)))) {
      toast.warning("Prices cannot be negative or invalid.");
      return;
    }

    const payload = {
      product_ids: selectedIds,
    };
    enteredPrices.forEach(([key, value]) => {
      payload[key] = Number(value);
    });

    updatePrices("products/bulk-update-prices", payload);
  };

  const isLoading = resget.isLoading || searchRes.isLoading;
  const vehicleTypes = [...new Set(products.map((product) => normalizeVehicleType(product.vehicle_type)).filter(Boolean))];
  const orderedVehicleTypes = [
    ...Object.keys(vehicleTypeConfig).filter((type) => vehicleTypes.includes(type)),
    ...vehicleTypes.filter((type) => !Object.prototype.hasOwnProperty.call(vehicleTypeConfig, type)),
  ];
  const filteredProducts = selectedVehicleType === "all"
    ? products
    : products.filter((product) => normalizeVehicleType(product.vehicle_type) === selectedVehicleType);
  const allVisibleSelected = filteredProducts.length > 0 && filteredProducts.every((product) => selectedIds.includes(product.id));

  return (
    <div className="NewsPage pr-10 max-lg:pr-6">
      <div className="TeamPageTop flex items-center justify-between gap-4 max-lg:flex-col max-lg:items-start">
        <div>
          <h6 className="bookingSectionh relative mb-1 px-3 text-[1rem] font-Mluvka capitalize">
            Bulk Update Prices
          </h6>
          <p className="px-3 text-sm text-[#7D8CA7]">
            Select products/cars and update one or more prices. Leave a price blank to keep it unchanged.
          </p>
        </div>
        <Link to="/products" className="rounded-full border border-[#D4DEF1] bg-white px-6 py-3 font-MluvkaBold text-primary">
          Back to Products
        </Link>
      </div>

      <form onSubmit={handleSubmit} className="mt-4 rounded-3xl bg-[#EFF4FD] p-6 max-lg:p-3">
        <div className="grid grid-cols-3 gap-3 max-lg:grid-cols-1">
          {[
            ["daily_price", "Daily Price"],
            ["weekly_price", "Weekly Price"],
            ["monthly_price", "Monthly Price"],
          ].map(([name, label]) => (
            <label key={name} className="text-sm text-[#7D8CA7]">
              <span className="mb-1 block">{label}</span>
              <input
                type="number"
                min="0"
                step="any"
                name={name}
                value={prices[name]}
                onChange={handlePriceChange}
                placeholder={`Enter ${label}`}
                className="h-[3rem] w-full rounded-xl border border-[#D4DEF1] bg-white px-4 text-[#101828] outline-none"
              />
            </label>
          ))}
        </div>

        <div className="mt-5 flex items-center justify-between gap-3 max-lg:flex-col max-lg:items-stretch">
          <input
            type="search"
            value={searchValue}
            onChange={(event) => {
              const value = event.target.value;
              setSearchValue(value);
              loadProducts(1, value);
            }}
            placeholder="Search by car/product name, model, year or slug..."
            className="h-[3rem] flex-1 rounded-full border border-[#D4DEF1] bg-white px-5 outline-none"
          />
          <button
            type="submit"
            disabled={updateRes.isLoading}
            className="rounded-full bg-primary px-6 py-3 font-MluvkaBold text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {updateRes.isLoading ? "Updating..." : `Update Selected (${selectedIds.length})`}
          </button>
        </div>

        <div className="mt-5 flex gap-3 overflow-x-auto pb-1">
          <button
            type="button"
            onClick={() => setSelectedVehicleType("all")}
            className={`flex min-w-[7.5rem] flex-col items-center justify-center gap-1 rounded-xl border px-4 py-3 text-sm font-MluvkaBold transition-all ${selectedVehicleType === "all"
              ? "border-primary bg-primary text-white shadow-[0_8px_18px_rgba(64,26,137,0.2)]"
              : "border-[#DCCFF1] bg-white text-primary hover:border-primary"
              }`}
          >
            <FaCar className="text-xl" />
            All
          </button>
          {orderedVehicleTypes.map((type) => {
            const config = vehicleTypeConfig[type] || { label: formatVehicleType(type), icon: FaCar };
            const Icon = config.icon;
            return (
              <button
                type="button"
                key={type}
                onClick={() => setSelectedVehicleType(type)}
                className={`flex min-w-[7.5rem] flex-col items-center justify-center gap-1 rounded-xl border px-4 py-3 text-sm font-MluvkaBold transition-all ${selectedVehicleType === type
                  ? "border-primary bg-primary text-white shadow-[0_8px_18px_rgba(64,26,137,0.2)]"
                  : "border-[#DCCFF1] bg-white text-primary hover:border-primary"
                  }`}
              >
                <Icon className="text-xl" />
                {config.label}
              </button>
            );
          })}
        </div>

        <div className="mt-5 overflow-hidden rounded-2xl border border-[#D4DEF1] bg-white">
          <div className="flex items-center justify-between border-b border-[#EEF2F7] px-4 py-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm font-MluvkaBold text-[#101828]">
              <input type="checkbox" checked={allVisibleSelected} onChange={toggleVisibleProducts} />
              Select visible products/cars
            </label>
            <span className="text-sm text-[#7D8CA7]">{selectedIds.length} selected</span>
          </div>

          {isLoading ? (
            <div className="p-6 text-center text-[#7D8CA7]">Loading products...</div>
          ) : filteredProducts.length ? (
            <div className="grid grid-cols-2 gap-3 p-4 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6 max-lg:p-3">
              {filteredProducts.map((product) => (
                <label
                  key={product.id}
                  className={`group relative flex min-h-[13rem] cursor-pointer flex-col rounded-2xl border p-3 transition-all ${selectedIds.includes(product.id)
                    ? "border-primary bg-[#F2F0FF] shadow-[0_8px_20px_rgba(64,26,137,0.12)]"
                    : "border-[#E6EBF4] bg-white hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-[0_8px_20px_rgba(18,38,63,0.08)]"
                    }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(product.id)}
                    onChange={() => toggleProduct(product.id)}
                    className="absolute left-3 top-3 z-10 h-4 w-4 cursor-pointer accent-[#401A89]"
                  />
                  <div className="mb-3 flex h-[7rem] items-center justify-center rounded-xl bg-[#F7F9FC] p-2">
                    {product.main_image ? (
                      <img src={product.main_image} alt={product.product_title || "product"} className="h-full w-full object-contain" />
                    ) : (
                      <span className="text-xs text-[#7D8CA7]">No image</span>
                    )}
                  </div>
                  <span className="mt-auto min-w-0">
                    <span className="block truncate font-MluvkaBold text-sm text-[#101828]" title={product.product_title || product.title}>
                      {product.product_title || product.title || "Untitled product"}
                    </span>
                    <span className="mt-1 block truncate text-xs text-[#7D8CA7]">
                      {product.catalog_title || product.vehicle_type || product.slug || "Product / Car"}
                    </span>
                    <span className="mt-1 block text-xs font-MluvkaBold text-primary">ID: {product.id}</span>
                  </span>
                  {selectedIds.includes(product.id) && (
                    <span className="absolute right-3 top-3 rounded-full bg-primary px-2 py-1 text-[10px] font-MluvkaBold text-white">Selected</span>
                  )}
                </label>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-[#7D8CA7]">No products or cars found in this vehicle type.</div>
          )}
        </div>

        <div className="mt-4">
          <Pagination
            current={currentPage}
            onChange={(page) => loadProducts(page, searchValue)}
            total={pagination?.total || 0}
            pageSize={1000}
            showSizeChanger={false}
          />
        </div>
      </form>
    </div>
  );
};

export default BulkUpdatePrices;
